import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { COLORS } from 'components/consts';
import { EMPTY_CELL_VALUE, Label12Regular } from 'components';
import { isNullOrUndefined } from 'utils';
import { EmissionsAllocationOverviewData } from 'api';
import { createHeaderWithEllipsis, createAggregatedMeasureValueCell, createRenewableUsageCell } from 'pages/authenticated/sites-emissions/components/site-emissions-table/helpers';
import { DECIMAL_PLACES, UNIT_LABELS } from './consts';

// Reusable Cell renderer for text columns with Tooltip
const renderTextCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue() as string;
  return value ? (
    <Tooltip title={value}>
      <Box>{value}</Box>
    </Tooltip>
  ) : (
    <Box sx={{ paddingLeft: '15px' }}>{EMPTY_CELL_VALUE}</Box>
  );
};

// Reusable Cell renderer for numeric columns
const renderNumericCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return isNullOrUndefined(value) ? EMPTY_CELL_VALUE : <Box>{value as number}</Box>;
};

// Reusable Cell renderer for numeric columns with units
const renderNumericWithUnitCell = ({
  cell,
  row,
  unitKey,
  unitLabel,
  decimalPlaces = DECIMAL_PLACES.DEFAULT
}: {
  cell: any;
  row: any;
  unitKey: string;
  unitLabel?: string;
  decimalPlaces?: number;
}) => {
  const value = cell.getValue();
  const unit = unitLabel || row.original[unitKey];

  if (isNullOrUndefined(value)) {
    return EMPTY_CELL_VALUE;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Box>{Number(value).toFixed(decimalPlaces)}</Box>
      <Label12Regular sx={{ ml: '5px', mt: '2px' }}>{unit}</Label12Regular>
    </Box>
  );
};

// Reusable AggregatedCell renderer for numeric columns with units
const renderAggregatedNumericWithUnitCell = ({
  cell,
  row,
  unitKey,
  unitLabel
}: {
  cell: any;
  row: any;
  unitKey: string;
  unitLabel?: string;
}) => {
  const value = cell.getValue();
  const unit = unitLabel || row.original[unitKey];

  return (
    <Box sx={{ display: 'flex', color: 'info.main' }}>
      <Box sx={{ color: COLORS.lightgray }}>{Number(value).toFixed(DECIMAL_PLACES.AGGREGATED)}</Box>
      <Label12Regular sx={{ ml: '5px', mt: '2px', color: COLORS.lightgray }}>{unit}</Label12Regular>
    </Box>
  );
};

// Reusable Cell renderer for standardized usage column
const createStandardizedUsageColumn = (key: string, label: string, unit: string) => ({
  Header: createHeaderWithEllipsis(label),
  header: label,
  accessorFn: (row: any) => row[key]?.measureValue,
  enableGrouping: false,
  aggregationFn: 'sum' as const,
  Cell: ({ row }: { row: any }) => {
    const value = row.original[key];
    if (isNullOrUndefined(value?.measureValue) || isNullOrUndefined(value?.measureUnit)) {
      return EMPTY_CELL_VALUE;
    }
    return (
      <Box>
        {`${Number(value.measureValue).toFixed(DECIMAL_PLACES.DEFAULT)} ${value.measureUnit}`}
      </Box>
    );
  },
  AggregatedCell: ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    return createAggregatedMeasureValueCell(value, unit);
  }
});

// Reusable Cell renderer for emission column
const createEmissionColumn = (accessorKey: string, header: string, size?: number) => ({
  header,
  accessorKey,
  enableGrouping: false,
  size,
  aggregationFn: 'sum' as const,
  Cell: ({ cell }: { cell: any }) =>
    renderNumericWithUnitCell({
      cell,
      row: {},
      unitKey: '',
      unitLabel: UNIT_LABELS.CO2
    }),
  AggregatedCell: ({ cell }: { cell: any }) =>
    renderAggregatedNumericWithUnitCell({
      cell,
      row: {},
      unitKey: '',
      unitLabel: UNIT_LABELS.CO2
    }),
});

export const getEmissionsAllocationColumns = (): MRT_ColumnDef<EmissionsAllocationOverviewData>[] => [
  {
    id: 'Properties',
    header: 'Properties',
    muiTableHeadCellProps: {},
    columns: [
      {
        header: 'Start Date',
        accessorKey: 'startDate',
        Header: createHeaderWithEllipsis('Start Date'),
        Cell: renderNumericCell,
      },
      {
        header: 'End Date',
        accessorKey: 'endDate',
        Header: createHeaderWithEllipsis('End Date'),
        Cell: renderNumericCell,
      },
      {
        header: 'Number of Days',
        accessorKey: 'daysCovered',
        Header: createHeaderWithEllipsis('Number of Days'),
        Cell: renderNumericCell,
      },
      {
        Header: createHeaderWithEllipsis("Number of Unique Account/Meters"),
        header: "Number of Unique Account/Meters",
        accessorKey: "numberOfUniqueCollectors",
        Cell: ({ cell }: { cell: any }) => {
          const value = cell.getValue();
          if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
          }
          return <Box>{(value as number)}</Box>;
        },
      },
      {
        header: 'Country',
        accessorKey: 'country',
        Header: createHeaderWithEllipsis('Country'),
        Cell: renderTextCell,
      },
      {
        header: 'Dimension',
        accessorKey: 'dimension',
        Header: createHeaderWithEllipsis('Dimension'),
        Cell: renderTextCell,
      },
      {
        header: 'Allocation Title',
        accessorKey: 'title',
        Header: createHeaderWithEllipsis('Allocation Title'),
        Cell: renderTextCell,
      },
      {
        header: 'Type of Site',
        accessorKey: 'sitetype',
        Header: createHeaderWithEllipsis('Type of Site'),
        Cell: renderTextCell,
      },
      {
        header: 'Business Unit',
        accessorKey: 'businessUnit',
        Header: createHeaderWithEllipsis('Business Unit'),
        Cell: renderNumericCell,
      },
      {
        header: 'Customer Site ID',
        accessorKey: 'customerSiteId',
        Header: createHeaderWithEllipsis('Customer Site ID'),
        Cell: renderNumericCell,
      },
      {
        header: 'Site',
        accessorKey: 'sitename',
        Header: createHeaderWithEllipsis('Site'),
        Cell: renderTextCell,
      },
      {
        header: 'Utility Type',
        accessorKey: 'utilityName',
        Header: createHeaderWithEllipsis('Utility Type'),
        Cell: renderTextCell,
      },
      {
        header: 'Estimation Methodology',
        accessorKey: 'estimationMethodology',
        Header: createHeaderWithEllipsis('Estimation Methodology'),
        Cell: renderTextCell,
      },
      {
        header: 'Scope',
        accessorKey: 'scope',
        Header: createHeaderWithEllipsis('Scope'),
        Cell: renderTextCell,
      },
      {
        header: 'Source',
        accessorKey: 'source',
        Header: createHeaderWithEllipsis('Source'),
        Cell: renderTextCell,
      },
    ],
  },
  {
    id: 'Usage',
    header: 'Usage - Location',
    columns: [
      {
        header: 'Usage',
        accessorKey: 'usage',
        Header: createHeaderWithEllipsis('Usage'),
        enableGrouping: false,
        aggregationFn: 'sum',
        Cell: ({ cell, row }) => renderNumericWithUnitCell({ cell, row, unitKey: 'unit' }),
        AggregatedCell: ({ cell, row }) =>
          cell.row.groupingColumnId === 'utilityName'
            ? renderAggregatedNumericWithUnitCell({ cell, row, unitKey: 'unit' })
            : null,
      },
      createStandardizedUsageColumn('standardizedUsageKWh', 'Standardized Usage (kWh)', UNIT_LABELS.KWH),
      createStandardizedUsageColumn('standardizedUsageML', 'Standardized Usage (ML)', UNIT_LABELS.ML),
      createStandardizedUsageColumn('standardizedUsageT', 'Standardized Usage (T)', UNIT_LABELS.T),
      createRenewableUsageCell("renewableEnergyUsage", "Renewable Energy Usage") as MRT_ColumnDef<EmissionsAllocationOverviewData>,
      createRenewableUsageCell("nonRenewableEnergyUsage", "Non-Renewable Energy Usage") as MRT_ColumnDef<EmissionsAllocationOverviewData>,
    ],
  },
  {
    id: 'Cost',
    header: 'Cost',
    columns: [
      {
        header: 'Cost',
        accessorKey: 'cost',
        Header: createHeaderWithEllipsis('Cost'),
        enableGrouping: false,
        size: 100,
        aggregationFn: 'sum',
        Cell: ({ cell, row }) => renderNumericWithUnitCell({ cell, row, unitKey: 'currency' }),
        AggregatedCell: ({ cell, row }) => renderAggregatedNumericWithUnitCell({ cell, row, unitKey: 'currency' }),
      },
    ],
  },
  {
    id: 'Emissions',
    header: 'Emissions - Location',
    columns: [
      createEmissionColumn('co2Emission', 'Emissions', 140),
      createEmissionColumn('co2OnlyEmission', 'CO2'),
      createEmissionColumn('ch4OnlyEmission', 'CH4 Fossil'),
      createEmissionColumn('ch4NonFossilEmission', 'CH4 Non-Fossil'),
      createEmissionColumn('n2oOnlyEmission', 'N2O'),
    ],
  },
  {
    id: 'Emissions - Market',
    header: 'Emissions - Market',
    columns: [
      createEmissionColumn('marketCo2Emission', 'Emissions'),
      createEmissionColumn('marketCo2OnlyEmission', 'CO2'),
      createEmissionColumn('marketCh4OnlyEmission', 'CH4'),
      {
        ...createEmissionColumn('marketCh4NonFossilEmission', 'CH4 Non-Fossil'),
        Header: createHeaderWithEllipsis('CH4 Non-Fossil - Market'),
      },
      createEmissionColumn('marketN2oOnlyEmission', 'N2O'),
    ],
  },
];